import { FormatterType } from '@fabric-ui/components';
import { HeadCellType, OptionQueryType } from '@fabric-ui/components/Table';
import { flex } from '@fabric-ui/theme';
import { ellipsis, generateOption } from '@fabric-ui/utils';
import { getNestedValue } from '@utils/utils';
import { GENERAL_ENTITY } from '@views/Sources/Mapping/constants';
import { styleNoUpperCase } from '../../../context/utils';
import { FieldMapping, FieldNameType, FieldValueType, FileFieldTypesMap, MappingTreeNode, MappingType } from './types';

export const itemStyle = (isSmall?: boolean) => ({
  ...flex.itemsCenter,
  gap: 1,
  flexShrink: 0,
  width: isSmall ? 266 : 282,
  height: isSmall ? 32 : 40,
  fontSize: isSmall ? 13 : 14,
  background: theme => theme.palette.white.main,
  pl: 2,
  pr: 0,
  borderRadius: '2px',
  svg: {
    color: 'var(--color-content-interactive-primary-default)',
    width: isSmall ? 18 : undefined,
    height: isSmall ? 18 : undefined,
  },
  span: ellipsis,
  transition: theme => theme.transitions.create(['box-shadow']),
  ':hover': {
    boxShadow: '0px 15px 20px rgba(0, 0, 0, 0.08)',
  },
});

// Splits a search query into whitespace-separated tokens and returns true if every token
// is contained in `text`. An empty query matches everything. Both inputs should be
// lower-cased by the caller (entitiesSearch is already lower-cased upstream).
export const matchesAllTokens = (text: string, query: string): boolean => {
  const tokens = query.split(/\s+/).filter(Boolean);
  return tokens.every(token => text.includes(token));
};

export const buildMappingKey = (item: Partial<FieldMapping>) =>
  `${item.entityName}.${item.modelFieldName}.replica-${item.replicaNumber ?? 0}`;

export const isScriptOrLiteral = (mappingType: MappingType) => [MappingType.Script, MappingType.Literal].includes(mappingType);

export const getEntitiesShouldBeOpen = (mappings: FieldMapping[]) =>
  mappings.reduce((obj, entity) => ({ ...obj, [entity.entityName]: true }), {});

export const getMapRowIndex = (mappings: FieldMapping[], key: string) => mappings.findIndex(item => buildMappingKey(item) === key);

export const createField = (label: string, isMissing: boolean, isExploded = false): MappingTreeNode => ({
  label: isExploded ? `${label} exploded` : label,
  isMissing,
  path: label,
  children: [],
  ...(isExploded ? { isExploded: true } : {}),
});

export const getMaxReplica = (mappings: FieldMapping[]) =>
  mappings.reduce((maxReplica, { replicaNumber }) => Math.max(replicaNumber ?? 0, maxReplica), 0);

export const generateNestedSqlAccessor = (path: string, rootObject = 'rawlogs.DOCUMENT', type?: FormatterType): string => {
  if (!path) {
    return rootObject;
  }

  const pathSegments = path.split('.');

  return pathSegments.reduce((currentExpression, segment, index) => {
    const isLastSegment = index === pathSegments.length - 1;
    const typeParam = isLastSegment && type ? `,${type.toUpperCase()}` : '';
    return `object_get(${currentExpression},'${segment}'${typeParam})`;
  }, rootObject);
};

export const formatExplodedFieldLabel = (field?: string, isExploded?: boolean) => field + (isExploded ? ' exploded' : '');

export const formatExplodedFieldValue = (field = '', isExploded = false) => JSON.stringify({ mappingValue: field, exploded: isExploded });

export const parseExplodedField = (value: string): { mappingValue: string; exploded: boolean } => {
  const defaultField = { mappingValue: value, exploded: false };
  try {
    const parsed = JSON.parse(value);
    if (typeof parsed === 'object' && parsed !== null) {
      return { mappingValue: parsed.mappingValue, exploded: parsed.exploded };
    }
  } catch {
    return defaultField;
  }

  return defaultField;
};

type BaseMappingItem = Omit<FieldMapping, 'fieldValueType' | 'mappingValue' | 'compositeValue' | 'exploded'>;

export const createSimpleFieldMapping = (baseItem: BaseMappingItem, mappingValue: string, mappingExploded?: boolean): FieldMapping =>
  ({
    ...baseItem,
    exploded: mappingExploded,
    fieldValueType: FieldValueType.MappingValue,
    mappingValue,
  }) as FieldMapping;

export const createCompositeFieldMapping = (baseItem: BaseMappingItem, mappingValue: string[]): FieldMapping =>
  ({
    ...baseItem,
    fieldValueType: FieldValueType.CompositeValue,
    compositeValue: {
      fields: mappingValue.map(
        v =>
          ({
            ...baseItem,
            fieldValueType: FieldValueType.MappingValue,
            ...parseExplodedField(v),
          }) as FieldMapping
      ),
    },
  }) as FieldMapping;

export const extractPropertiesFromScript = (script: string): string[] => {
  const regex = /row(?:\[['"]([^'"]+)['"]\]|\.get\(\s*['"]([^'"]+)['"](?:,\s*[^)]*)?\))/g;

  const allMatches = Array.from(script.matchAll(regex), match => match[1] || match[2]);

  return Array.from(new Set(allMatches)).sort();
};

export const getHeadCells = ({
  headers,
  instanceId,
  lastWfRun,
  headerTypes = {},
}: {
  headers: string[];
  instanceId?: string;
  lastWfRun?: string;
  headerTypes?: FileFieldTypesMap;
}): HeadCellType[] => [
  { id: 'avalor_unique_key', isKey: true, hidden: true },
  ...headers.map(id => ({
    id,
    label: id,
    formatter: (val, row) => {
      const value = getNestedValue(id, row);

      return typeof value === 'object' || typeof value === 'boolean' ? JSON.stringify(value) : value;
    },
    type: headerTypes[id],
    sx: styleNoUpperCase,
    asyncGetOptions:
      instanceId && lastWfRun
        ? (search: string) => {
            const accessor = generateNestedSqlAccessor(id);
            const expression = headerTypes[id] === FormatterType.array ? `EXPLODE(${accessor})` : accessor;
            return {
              type: OptionQueryType.SQL,
              key: `options${id}${search}`,
              sql: `SELECT distinct(${expression}) as value
                          FROM rawlogs
                          WHERE AVALOR_CONTEXT(rawlogs
                              , 'workflowId') = '${lastWfRun}'
                            AND rawlogs.DSI_ID = '${instanceId}' ${search ? `AND value LIKE '%${search}%'` : ''}
                          ORDER BY value DESC
                          OFFSET 0 LIMIT 100`,
              onSuccess: d => d.map(r => generateOption(typeof r.value === 'object' ? JSON.stringify(r.value) : r.value)),
            };
          }
        : undefined,
  })),
];

export const flattenMappings = (mappings: MappingTreeNode[]): MappingTreeNode[] =>
  mappings.flatMap(mapping => [mapping, ...(mapping.children ? flattenMappings(mapping.children) : [])]);

export const resolvePathSegment = (current: any, key: string): any => {
  if (current == null || typeof current !== 'object') return undefined;
  if (Array.isArray(current)) {
    const found = current.find(el => el && typeof el === 'object' && key in el);
    return found ? found[key] : undefined;
  }
  return key in current ? current[key] : undefined;
};

export const isMappingItemPathMissing = (field: string, parsedFields: string[], parsedPreviewData: any): boolean =>
  !parsedFields.includes(field) &&
  parsedPreviewData?.[field] === undefined &&
  !!field &&
  !Array.isArray(field) &&
  field.split('.').reduce(resolvePathSegment, parsedPreviewData) === undefined;

export const getNextLevelFieldGroupName = (groupName: string, itemsCount: number) => `${groupName} Level ${itemsCount + 1}`;

export const isMappingExploded = (mapping: FieldMapping) =>
  (mapping.fieldValueType === FieldValueType.MappingValue && !!mapping.exploded) ||
  (mapping.fieldValueType === FieldValueType.CompositeValue && !!mapping.compositeValue?.fields?.some(f => f.exploded));

export const getDefaultFieldName = mapping =>
  `${mapping.modelName}_${mapping.metadataName}_${mapping.modelFieldName}_${mapping.entityName}_${mapping.replicaNumber}`;

export const toExplodedPath = (fields?: MappingTreeNode[], fieldPath?: string) => {
  const fieldTypesMap = getFieldTypePath(fields ?? [], fieldPath);
  const parts = fieldPath?.split('.') ?? [];

  const { explodedParts } = parts.reduce(
    (acc, segment, index) => {
      const newCurrentPath = acc.currentPath ? `${acc.currentPath}.${segment}` : segment;
      const type = fieldTypesMap[newCurrentPath];
      const explodedSegment = type === FormatterType.array && index < parts.length - 1 ? `${segment}[*]` : segment;

      return {
        currentPath: newCurrentPath,
        explodedParts: [...acc.explodedParts, explodedSegment],
      };
    },
    { currentPath: '', explodedParts: [] as string[] }
  );

  return explodedParts.join('.');
};

export const getMappingMetadata = (mappings: Array<FieldMapping & { isAutoMapped?: boolean }>, fields?: MappingTreeNode[]) => {
  const handleMappingValue = (mapping: Omit<FieldMapping, 'fieldNameType' | 'fieldValueType'>, fieldValueType: FieldValueType) =>
    mapping.exploded && fieldValueType === FieldValueType.MappingValue
      ? toExplodedPath(fields, mapping.mappingValue)
      : mapping.mappingValue;

  return {
    fieldMapping: mappings.map(({ fieldNameType, fieldValueType, isAutoMapped, ...mapping }) => ({
      ...mapping,
      entityName: mapping.entityName === GENERAL_ENTITY ? undefined : mapping.entityName,
      ...(fieldValueType === FieldValueType.CompositeValue
        ? {
            compositeValue: {
              fields:
                mapping.compositeValue?.fields.map(({ fieldNameType, fieldValueType, ...field }) => ({
                  ...field,
                  mappingValue: handleMappingValue(field, fieldValueType),
                  entityName: field.entityName === GENERAL_ENTITY ? undefined : field.entityName,
                  mappingType: MappingType.Path,
                })) ?? [],
            },
          }
        : {
            mappingValue: handleMappingValue(mapping, fieldValueType),
          }),
    })),
  };
};

export const processMappings = (data: any): FieldMapping[] => {
  const handleMapping = (mapping: any) => {
    const newMapping = { ...mapping };
    if (newMapping.exploded && newMapping.mappingType === MappingType.Path && typeof newMapping.mappingValue === 'string') {
      newMapping.mappingValue = newMapping.mappingValue.replace(/\[\*\]\./g, '.');
    }
    return newMapping;
  };

  return data.map(row => {
    const newRow = {
      ...handleMapping(row),
      entityName: row.entityName || GENERAL_ENTITY,
      modelFieldName: row.modelFieldName || row.modelName || row.metadataName,
      ...(row.metadataName
        ? { metadataName: row.metadataName, fieldNameType: FieldNameType.Metadata }
        : { modelName: row.modelName || row.modelFieldName, fieldNameType: FieldNameType.Model }),
    };

    if (newRow.compositeValue?.fields?.length > 0) {
      const { compositeValue, ...restOfNewRow } = newRow;

      const correctedCompositeFields = compositeValue.fields.map(field => ({
        ...handleMapping(field),
        fieldValueType: field.fieldValueType || FieldValueType.MappingValue,
      }));

      return {
        ...restOfNewRow,
        mappingType: MappingType.Composite,
        fieldValueType: FieldValueType.CompositeValue,
        compositeValue: { ...compositeValue, fields: correctedCompositeFields },
        defaultFieldName: getDefaultFieldName(newRow),
      };
    }
    return { ...newRow, fieldValueType: FieldValueType.MappingValue, defaultFieldName: getDefaultFieldName(newRow) };
  });
};

export const parseDataFields = (dataFields: MappingTreeNode[], explodedField: string | undefined) => {
  const parseExplodedChildren = (field: MappingTreeNode) =>
    field.isExploded ? [] : field.children.map(child => ({ ...child, label: `${child.path} exploded`, isExploded: true }));

  return dataFields.reduce(
    (list, field) => [
      ...list,
      ...(field.path === explodedField
        ? [
            { ...field, children: [], isExplodedParent: true },
            { ...field, label: `${field.path} exploded`, isExploded: true, children: parseExplodedChildren(field) },
          ]
        : [{ ...field, children: parseDataFields(field.children, explodedField) }]),
    ],
    [] as MappingTreeNode[]
  );
};

const findNodeByPath = (nodes: MappingTreeNode[], fieldPath: string): MappingTreeNode | undefined => {
  const directMatch = nodes.find(node => node.path === fieldPath);
  if (directMatch) {
    return directMatch;
  }

  return nodes.reduce((found: MappingTreeNode | undefined, node) => {
    if (found) return found;
    if (node.children && node.children.length > 0) {
      const foundInChild = findNodeByPath(node.children, fieldPath);
      if (foundInChild) {
        return foundInChild;
      }
    }
    return found;
  }, undefined);
};

export const getFieldTypePath = (fieldsList: MappingTreeNode[], fieldPath?: string): Record<string, string> => {
  const segments = fieldPath?.split('.') ?? [];

  const { result: finalResult } = segments.reduce(
    (acc: any, segment: any) => {
      if (acc.stop) {
        return acc;
      }

      const newCurrentPath = acc.currentPath ? `${acc.currentPath}.${segment}` : segment;
      const foundNode = findNodeByPath(fieldsList, newCurrentPath);

      if (foundNode?.path) {
        return {
          currentPath: newCurrentPath,
          result: {
            ...acc.result,
            [foundNode.path]: foundNode.type,
          },
          stop: false,
        };
      }
      return { ...acc, stop: true };
    },
    { currentPath: '', result: {}, stop: false } as any
  );

  return finalResult;
};
